import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Button, Tag, message, Spin, Radio, Space, Modal, Input } from 'antd';
import { CreditCardOutlined, WalletOutlined, BankOutlined, CheckCircleFilled, SecurityScanOutlined } from '@ant-design/icons';
import { userService } from '../services/api';
import './CustomerDashboard.css';

const { Title, Text, Paragraph } = Typography;

const paymentMethods = [
  { id: 'cash_on_delivery', name: 'Cash on Delivery', icon: <BankOutlined />, description: 'Pay when you receive your order', color: '#2d5a3d' },
  { id: 'digital_wallet', name: 'Digital Wallet', icon: <WalletOutlined />, description: 'eSewa, Khalti, IME Pay', color: '#f1a134' },
  { id: 'credit_card', name: 'Credit / Debit Card', icon: <CreditCardOutlined />, description: 'Visa, Mastercard, American Express', color: '#2e9bd6' },
];

const PaymentMethods = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentMethod, setCurrentMethod] = useState('cash_on_delivery');
  const [passwordModal, setPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [pendingMethod, setPendingMethod] = useState(null);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const res = await userService.getProfile();
      const data = res.data?.data || res.data;
      if (data.default_payment_method) setCurrentMethod(data.default_payment_method);
    } catch { console.error('Failed to fetch payment methods'); }
    finally { setLoading(false); }
  };

  const handleSelect = (method) => {
    setPendingMethod(method);
    setPasswordModal(true);
  };

  const handleConfirm = async () => {
    if (!password) { message.warning('Please enter your password'); return; }
    setSaving(true);
    try {
      await userService.updatePaymentMethods({ default_payment_method: pendingMethod });
      message.success('Payment method updated!');
      setCurrentMethod(pendingMethod);
      setPasswordModal(false);
      setPassword('');
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', paddingTop: 80 }}><Spin size="large" /></div>;

  return (
    <div className="customer-page" style={{ padding: 0 }}>
      <div className="customer-page-header">
        <Title level={2}>Payment Methods</Title>
        <Paragraph>Manage your default payment method for faster checkout.</Paragraph>
      </div>

      <Row gutter={[16, 16]}>
        {paymentMethods.map((method) => {
          const isActive = currentMethod === method.id;
          return (
            <Col xs={24} sm={8} key={method.id}>
              <Card
                className="customer-card"
                hoverable
                onClick={() => handleSelect(method.id)}
                style={{
                  cursor: 'pointer',
                  border: isActive ? '2px solid #52b788' : '1px solid #e8f0e8',
                  position: 'relative',
                  transition: 'all 0.2s',
                }}
              >
                {isActive && <CheckCircleFilled style={{ position: 'absolute', top: 12, right: 12, color: '#52b788', fontSize: 20 }} />}
                <div style={{ textAlign: 'center', padding: '12px 0' }}>
                  <div style={{ width: 56, height: 56, borderRadius: 16, background: `${method.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                    {React.cloneElement(method.icon, { style: { fontSize: 26, color: method.color } })}
                  </div>
                  <Title level={5} style={{ margin: 0, color: '#2d5a3d' }}>{method.name}</Title>
                  <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 4 }}>{method.description}</Paragraph>
                  <Radio checked={isActive} style={{ marginTop: 8 }} />
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Card className="customer-card" style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
          <SecurityScanOutlined style={{ fontSize: 20, color: '#52b788' }} />
          <Text type="secondary">Your payment information is stored securely and encrypted.</Text>
        </div>
      </Card>

      <Modal title="Confirm Payment Method" open={passwordModal} onCancel={() => { setPasswordModal(false); setPassword(''); }} onOk={handleConfirm} okText="Save" confirmLoading={saving}>
        <Paragraph>Set <Text strong>{paymentMethods.find((m) => m.id === pendingMethod)?.name}</Text> as your default payment method?</Paragraph>
        <Input.Password placeholder="Enter your password to confirm" value={password} onChange={(e) => setPassword(e.target.value)} style={{ borderRadius: 10 }} />
      </Modal>
    </div>
  );
};

export default PaymentMethods;
